import { z } from "zod";
import {
  AVAILABLE_FROM,
  EDUCATION_LEVELS,
  LIVING_ARRANGEMENTS,
  PROVINCE_CODES,
} from "../catalogs";
import { emailSchema, isoDateTimeString, uuidSchema } from "./common";

// Re-export shared enums from postings + kyc to avoid drift.
import {
  employmentTypeSchema,
  experienceDurationSchema,
  genderSchema,
  salaryTypeSchema,
  workerPositionSchema,
} from "./postings";
import { kycStatusSchema } from "./kyc";

export {
  employmentTypeSchema,
  experienceDurationSchema,
  genderSchema,
  salaryTypeSchema,
  workerPositionSchema,
};
export { kycStatusSchema };
export const globalRoleSchema = z.enum(["USER", "ADMIN"]);
export const orgRoleSchema = z.enum(["OWNER", "HR"]);
/**
 * Derived account classification (not a DB column). Computed from globalRole +
 * profiles + memberships: ADMIN (globalRole), EMPLOYER (has org membership /
 * employer profile), else WORKER. Drives the admin Accounts list filter.
 */
export const accountTypeSchema = z.enum(["WORKER", "EMPLOYER", "ADMIN"]);
export const userStatusSchema = z.enum(["ACTIVE", "SUSPENDED", "DELETED"]);
export const livingArrangementSchema = z.enum(LIVING_ARRANGEMENTS);
export const educationLevelSchema = z.enum(EDUCATION_LEVELS);
export const provinceCodeSchema = z.enum(PROVINCE_CODES);
export const availableFromSchema = z.enum(AVAILABLE_FROM);

// Same as postings.ts — no transform to keep zod-openapi happy.
const salaryVndInputSchema = z.union([
  z.number().int().nonnegative(),
  z.bigint(),
]);
const salaryOutputSchema = z.union([z.string(), z.number(), z.bigint()]);

/** Desired salary expressed independently per unit. Worker can express
 *  multiple ranges (e.g. willing to take FT 8–12tr/tháng OR PT 30–50k/giờ).
 *  Replaces the legacy scalar `desiredSalaryMin / Max / Type` columns —
 *  legacy columns are kept readable for backward compat and synthesized
 *  into this map server-side until the cleanup migration drops them. */
const salaryBoundOutputSchema = z
  .union([z.string(), z.number(), z.bigint()])
  .nullable()
  .optional();
const salaryBoundInputSchema = z
  .union([z.number().int().nonnegative(), z.bigint()])
  .nullable()
  .optional();

const salaryRangeOutputSchema = z.object({
  min: salaryBoundOutputSchema,
  max: salaryBoundOutputSchema,
});
const salaryRangeInputSchema = z.object({
  min: salaryBoundInputSchema,
  max: salaryBoundInputSchema,
});

export const desiredSalaryRangesOutputSchema = z
  .object({
    PER_MONTH: salaryRangeOutputSchema.optional(),
    PER_HOUR: salaryRangeOutputSchema.optional(),
    PER_SHIFT: salaryRangeOutputSchema.optional(),
    PER_DAY: salaryRangeOutputSchema.optional(),
  })
  .nullable()
  .optional();

export const desiredSalaryRangesInputSchema = z
  .object({
    PER_MONTH: salaryRangeInputSchema.optional(),
    PER_HOUR: salaryRangeInputSchema.optional(),
    PER_SHIFT: salaryRangeInputSchema.optional(),
    PER_DAY: salaryRangeInputSchema.optional(),
  })
  .nullable()
  .optional();

export const languageEntrySchema = z.object({
  lang: z.string().min(2).max(8),
  level: z.enum(["BASIC", "INTERMEDIATE", "FLUENT"]),
});

// Linked-branch summary returned alongside an experience entry. Only
// present when the worker picked a real branch (chi nhánh / OrgAddress)
// from search. UI renders `alias` (trusted) and `orgName` for context,
// plus an optional logo + verified badge.
export const workerExperienceBranchSchema = z.object({
  id: uuidSchema, // orgAddressId
  orgId: uuidSchema,
  orgName: z.string(),
  alias: z.string(),
  district: z.string(),
  city: z.string(),
  logoPublicUrl: z.string().nullable(),
  kycVerified: z.boolean(),
});

// Period is a month+year range. `current=true` means the experience is
// ongoing — `endMonth`/`endYear` must be null in that case. Months are
// 1-12. Years are realistic working-life years; backend enforces.
const monthSchema = z.number().int().min(1).max(12);
const yearSchema = z.number().int().min(1950).max(2100);

export const workerExperienceSchema = z.object({
  id: uuidSchema,
  position: workerPositionSchema,
  startMonth: monthSchema,
  startYear: yearSchema,
  endMonth: monthSchema.nullable(),
  endYear: yearSchema.nullable(),
  current: z.boolean(),
  // Freetext fallback. Only meaningful when `branch` is null.
  placeName: z.string().nullable().optional(),
  // Worker-written summary of the role. Surfaced to employer/admin as the
  // primary hiring signal. Optional, capped at 280 chars on write.
  description: z.string().nullable().optional(),
  // Linked-branch summary. Non-null = system-verified place, null = freetext.
  branch: workerExperienceBranchSchema.nullable(),
  createdAt: isoDateTimeString,
});

// Either link to a real branch (`orgAddressId`) OR provide a freetext
// `placeName`. Backend ignores `placeName` when `orgAddressId` is set.
// When `current=true`, omit/null `endMonth` + `endYear`. Otherwise both required.
export const createWorkerExperienceRequestSchema = z
  .object({
    position: workerPositionSchema,
    startMonth: monthSchema,
    startYear: yearSchema,
    endMonth: monthSchema.nullable().optional(),
    endYear: yearSchema.nullable().optional(),
    current: z.boolean().default(false),
    orgAddressId: uuidSchema.nullable().optional(),
    placeName: z.string().max(120).nullable().optional(),
    description: z.string().trim().max(280).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.current) {
      if (v.endMonth != null || v.endYear != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "endMonth/endYear must be null when current=true",
          path: ["endMonth"],
        });
      }
      return;
    }
    if (v.endMonth == null || v.endYear == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endMonth and endYear required when current=false",
        path: ["endMonth"],
      });
      return;
    }
    const start = v.startYear * 12 + (v.startMonth - 1);
    const end = v.endYear * 12 + (v.endMonth - 1);
    if (end < start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "end must be on or after start",
        path: ["endMonth"],
      });
    }
  });

// Profile-completion gap. Backend computes the list of missing sections
// (mirroring `recomputeCompletion` weights) and ships each one with the
// mobile route to fix it. Mobile maps `id` → label via i18n and uses
// `path` as the CTA target — no client-side "what's missing" logic.
export const completionGapIdSchema = z.enum([
  "avatar",
  "basic",
  "jobPosition",
  "jobEmployment",
  "jobSalary",
  "experience",
  "physiqueBio",
  "skillsAdvantages",
  "kyc",
]);
export type CompletionGapId = z.infer<typeof completionGapIdSchema>;

export const completionGapSchema = z.object({
  id: completionGapIdSchema,
  weight: z.number().int().positive(),
  path: z.string(),
});
export type CompletionGap = z.infer<typeof completionGapSchema>;

export const workerProfileSchema = z.object({
  userId: uuidSchema,
  // L1
  fullName: z.string().nullable().optional(),
  birthYear: z.number().int().min(1940).max(2020).nullable().optional(),
  gender: genderSchema.nullable().optional(),
  // Location. `currentDistrict` kept (= ward name) for legacy matching; the
  // 2025 ward triplet is the canonical picker output. See wards-2025 catalog.
  currentDistrict: z.string().nullable().optional(),
  currentWardCode: z.string().nullable().optional(),
  currentWardName: z.string().nullable().optional(),
  currentWardProvinceCode: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  // L2 mong muốn công việc
  desiredPositions: z.array(workerPositionSchema),
  employmentTypes: z.array(employmentTypeSchema),
  desiredAreas: z.array(z.string()),
  desiredSalaryMin: salaryOutputSchema.nullable().optional(),
  desiredSalaryMax: salaryOutputSchema.nullable().optional(),
  desiredSalaryType: salaryTypeSchema.nullable().optional(),
  desiredSalaryRanges: desiredSalaryRangesOutputSchema,
  availableFrom: availableFromSchema.nullable().optional(),
  // Contact (gamified, optional). Shared with employer at invite COMMIT.
  contactPhone: z.string().nullable().optional(),
  zaloHandle: z.string().nullable().optional(),
  facebookHandle: z.string().nullable().optional(),
  instagramHandle: z.string().nullable().optional(),
  // L2 thể hình + bio
  heightCm: z.number().int().nullable().optional(),
  weightKg: z.number().int().nullable().optional(),
  bioShort: z.string().nullable().optional(),
  // L2 skills + advantages + languages
  skills: z.array(z.string()),
  advantages: z.array(z.string()),
  languages: z.array(languageEntrySchema).nullable().optional(),
  // L2 logistics
  hasMotorbike: z.boolean(),
  hasVisibleTattoo: z.boolean(),
  // L3 optional
  education: educationLevelSchema.nullable().optional(),
  hometown: provinceCodeSchema.nullable().optional(),
  livingArrangement: livingArrangementSchema.nullable().optional(),
  fullBodyImageKeys: z.array(z.string()),
  // GPS
  currentLat: z.number().nullable().optional(),
  currentLng: z.number().nullable().optional(),
  // KYC
  kycStatus: kycStatusSchema,
  kycVerifiedAt: isoDateTimeString.nullable().optional(),
  // Discoverability — "Đang tìm việc" toggle (Tìm ứng viên, docs/01-product.md)
  openToWork: z.boolean(),
  openToWorkUpdatedAt: isoDateTimeString.nullable().optional(),
  // Stats
  totalApplications: z.number().int(),
  totalInvites: z.number().int(),
  completionPct: z.number().int().min(0).max(100),
  // Sections that would still add points if completed. Empty array means
  // the profile is fully filled (completionPct === 100). Computed fresh
  // by the backend serializer — never persisted.
  completionGaps: z.array(completionGapSchema).default([]),
  // Experiences inline
  experiences: z.array(workerExperienceSchema).default([]),
  // Timestamps
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
});

export const employerProfileSchema = z.object({
  userId: uuidSchema,
  fullName: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
});

/** Membership listed inside /me payload. */
export const userMembershipSchema = z.object({
  orgId: uuidSchema,
  orgName: z.string(),
  role: orgRoleSchema,
  joinedAt: isoDateTimeString,
});

export const userMeSchema = z.object({
  id: uuidSchema,
  email: z.string().nullable().optional(),
  emailVerifiedAt: isoDateTimeString.nullable().optional(),
  // Account phone + identity ladder (SCRUM-71). phone is normalized (84xxxxxxxxx).
  phone: z.string().nullable().optional(),
  phoneVerifiedAt: isoDateTimeString.nullable().optional(),
  identityLevel: z.enum(["U0_NEW", "U1_CONTACT", "U2_IDENTITY"]).optional(),
  globalRole: globalRoleSchema,
  status: userStatusSchema,
  workerProfile: workerProfileSchema.nullable().optional(),
  employerProfile: employerProfileSchema.nullable().optional(),
  memberships: z.array(userMembershipSchema),
  createdAt: isoDateTimeString,
  updatedAt: isoDateTimeString,
});

export const patchMeWorkerRequestSchema = z.object({
  // L1
  fullName: z.string().min(1).optional(),
  birthYear: z.number().int().min(1940).max(2020).nullable().optional(),
  gender: genderSchema.nullable().optional(),
  // Location. Ward picker sends the triplet; `currentDistrict` (= ward name)
  // is written alongside so legacy district matching keeps working.
  currentDistrict: z.string().nullable().optional(),
  currentWardCode: z.string().nullable().optional(),
  currentWardName: z.string().nullable().optional(),
  currentWardProvinceCode: z.string().nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
  // L2 mong muốn
  desiredPositions: z.array(workerPositionSchema).max(10).optional(),
  employmentTypes: z.array(employmentTypeSchema).max(4).optional(),
  desiredAreas: z.array(z.string()).max(20).optional(),
  desiredSalaryMin: salaryVndInputSchema.nullable().optional(),
  desiredSalaryMax: salaryVndInputSchema.nullable().optional(),
  desiredSalaryType: salaryTypeSchema.nullable().optional(),
  desiredSalaryRanges: desiredSalaryRangesInputSchema,
  availableFrom: availableFromSchema.nullable().optional(),
  // Contact (gamified, optional)
  contactPhone: z.string().max(20).nullable().optional(),
  zaloHandle: z.string().max(60).nullable().optional(),
  facebookHandle: z.string().max(60).nullable().optional(),
  instagramHandle: z.string().max(60).nullable().optional(),
  // L2 thể hình + bio
  heightCm: z.number().int().min(120).max(220).nullable().optional(),
  weightKg: z.number().int().min(30).max(200).nullable().optional(),
  bioShort: z.string().max(300).nullable().optional(),
  // L2 skills
  skills: z.array(z.string()).max(30).optional(),
  advantages: z.array(z.string()).max(20).optional(),
  languages: z.array(languageEntrySchema).max(5).nullable().optional(),
  // L2 logistics flags
  hasMotorbike: z.boolean().optional(),
  hasVisibleTattoo: z.boolean().optional(),
  // L3
  education: educationLevelSchema.nullable().optional(),
  hometown: provinceCodeSchema.nullable().optional(),
  livingArrangement: livingArrangementSchema.nullable().optional(),
  fullBodyImageKeys: z.array(z.string()).max(5).optional(),
  // GPS
  currentLat: z.number().min(-90).max(90).nullable().optional(),
  currentLng: z.number().min(-180).max(180).nullable().optional(),
  // Discoverability — "Đang tìm việc" toggle (Tìm ứng viên, docs/01-product.md)
  openToWork: z.boolean().optional(),
});

/** Lightweight dashboard counters for the worker profile tab.
 *  - pendingApplications: applications still in APPLIED state (awaiting employer action)
 *  - recentInvites7d: invites committed in the last 7 days
 *  - applyQuotaRemaining / applyQuotaTotal: today's apply quota (resets 00:00 local).
 *    Tier depends on KYC — see APPLY_LIMIT_NO_KYC / APPLY_LIMIT_KYC. */
export const workerDashboardStatsSchema = z.object({
  pendingApplications: z.number().int().nonnegative(),
  recentInvites7d: z.number().int().nonnegative(),
  applyQuotaRemaining: z.number().int().nonnegative(),
  applyQuotaTotal: z.number().int().nonnegative(),
});

export const patchMeEmployerRequestSchema = z.object({
  fullName: z.string().min(1).max(100).optional(),
  jobTitle: z.string().max(100).nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
});

export const patchMeWorkerLocationRequestSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export const postMeAvatarRequestSchema = z.object({
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(10 * 1024 * 1024),
});

export const postMeAvatarResponseSchema = z.object({
  uploadUrl: z.string().url(),
  method: z.literal("PUT").optional(),
  headers: z.record(z.string(), z.string()).optional(),
  key: z.string(),
  publicUrl: z.string().url(),
  expiresAt: isoDateTimeString,
});

/** Anonymized worker CV card cho employer xem trước khi mời phỏng vấn.
 * Không bao gồm fullName chính xác, phone, email — chỉ initial mask. */
export const anonymizedWorkerCardSchema = z.object({
  workerUserId: uuidSchema,
  maskedName: z.string(), // "Bạn N."
  age: z.number().int().nullable(),
  gender: genderSchema.nullable(),
  currentDistrict: z.string().nullable(),
  distanceKm: z.number().nullable().optional(),
  heightCm: z.number().int().nullable(),
  weightKg: z.number().int().nullable(),
  desiredPositions: z.array(workerPositionSchema),
  employmentTypes: z.array(employmentTypeSchema),
  availableFrom: availableFromSchema.nullable(),
  bioShort: z.string().nullable(),
  skills: z.array(z.string()),
  advantages: z.array(z.string()),
  languages: z.array(languageEntrySchema).nullable(),
  experiences: z.array(workerExperienceSchema),
  avatarUrl: z.string().nullable(),
  fullBodyImageUrls: z.array(z.string()),
  kycStatus: kycStatusSchema,
  emailVerified: z.boolean(),
  totalApplications: z.number().int(),
  totalInvites: z.number().int(),
  completionPct: z.number().int(),
  memberSince: isoDateTimeString,
  lastActiveBucket: z
    .enum(["TODAY", "THIS_WEEK", "THIS_MONTH", "OLDER"])
    .nullable(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
});

/** Public profile shown on rating list, applicant detail (after invite commit), etc. */
export const publicWorkerSummarySchema = z.object({
  userId: uuidSchema,
  fullName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  currentDistrict: z.string().nullable().optional(),
  kycVerifiedAt: isoDateTimeString.nullable().optional(),
  ratingAvg: z.number().min(0).max(5).nullable(),
  ratingCount: z.number().int().min(0),
});

/** Email verification request/verify pair. */
export const emailRequestVerifyRequestSchema = z.object({
  email: emailSchema,
});

export const emailRequestVerifyResponseSchema = z.object({
  sentAt: isoDateTimeString,
  expiresAt: isoDateTimeString,
});

export const emailVerifyRequestSchema = z.object({
  token: z.string().min(8),
});

export const emailVerifyResponseSchema = z.object({
  emailVerifiedAt: isoDateTimeString,
});

/** Account deletion (Apple Guideline 5.1.1(v)).
 *  Soft-delete: marks the user as deleted, scrubs PII fields, revokes
 *  sessions and detaches push tokens. Confirmation string must match a
 *  client-side literal (e.g. "DELETE") so accidental taps don't wipe data. */
export const deleteMeRequestSchema = z.object({
  confirmation: z.literal("DELETE"),
});

export const deleteMeResponseSchema = z.object({
  deletedAt: isoDateTimeString,
});

export type GlobalRole = z.infer<typeof globalRoleSchema>;
export type OrgRole = z.infer<typeof orgRoleSchema>;
export type AccountType = z.infer<typeof accountTypeSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;
export type WorkerProfile = z.infer<typeof workerProfileSchema>;
export type WorkerExperience = z.infer<typeof workerExperienceSchema>;
export type CreateWorkerExperienceRequest = z.infer<
  typeof createWorkerExperienceRequestSchema
>;
export type EmployerProfile = z.infer<typeof employerProfileSchema>;
export type UserMembership = z.infer<typeof userMembershipSchema>;
export type UserMe = z.infer<typeof userMeSchema>;
export type PatchMeWorkerRequest = z.infer<typeof patchMeWorkerRequestSchema>;
export type PatchMeEmployerRequest = z.infer<
  typeof patchMeEmployerRequestSchema
>;
export type PatchMeWorkerLocationRequest = z.infer<
  typeof patchMeWorkerLocationRequestSchema
>;
export type PostMeAvatarRequest = z.infer<typeof postMeAvatarRequestSchema>;
export type PostMeAvatarResponse = z.infer<typeof postMeAvatarResponseSchema>;
export type AnonymizedWorkerCard = z.infer<typeof anonymizedWorkerCardSchema>;
export type PublicWorkerSummary = z.infer<typeof publicWorkerSummarySchema>;
export type LanguageEntry = z.infer<typeof languageEntrySchema>;
export type EmailRequestVerifyRequest = z.infer<
  typeof emailRequestVerifyRequestSchema
>;
export type EmailRequestVerifyResponse = z.infer<
  typeof emailRequestVerifyResponseSchema
>;
export type EmailVerifyRequest = z.infer<typeof emailVerifyRequestSchema>;
export type EmailVerifyResponse = z.infer<typeof emailVerifyResponseSchema>;
export type DeleteMeRequest = z.infer<typeof deleteMeRequestSchema>;
export type DeleteMeResponse = z.infer<typeof deleteMeResponseSchema>;
export type WorkerDashboardStats = z.infer<typeof workerDashboardStatsSchema>;
export type DesiredSalaryRanges = z.infer<
  typeof desiredSalaryRangesInputSchema
>;
